import { computed, observable, runInAction, when, IReactionDisposer } from 'mobx'
import { $RootStore } from '../../../src/stores/RootStore'
import analytics from '../../../src/stores/analytics'
import steps from './stepData/allSteps'
import setupTasksData, { SetupTaskData, SetupTaskId } from './setupTasksData'
import { getTaskStatus, validateStepData } from './setupHelpers'
import { SetupStep, ToastData } from './stepData/d'
import gmbOnboardingSteps from './stepData/gmbOnboardingSteps'
import { hasCapability, sessionAccessOf } from '../../lib/sessionCapabilities'
import { WebEditorModal } from '../../../shared-lib/lib/MessageHandler'

export type PlatformStepData = {
  id: string
  startAction?: (rootStore: $RootStore) => Promise<void>
  check?: (rootStore: $RootStore) => boolean
  __reset?: (rootStore: $RootStore) => Promise<void>
}

type ManualStepAction =
  'addBooking' | 'createBirthdayRewardEmail' | 'optimisedWebsite' | 'syncReviews'
  | 'addedAftercareInfo' | 'enabledLoyaltyScheme' | 'enabledReferralScheme' | 'createdLapsedEmail'
  | 'savedSocialPost' | 'watchVideo' | 'setupCancellationPolicy' | 'addedTermsAndConditions' | 'usedInlineWebsiteEditor'
  | 'joinWhatsapp' | 'viewPodcast' | 'viewMiniLinkedIn' | 'followSoloInstagram' | 'setupNotificationPreferences'

class BaseSetupStore {
  rootStore: $RootStore
  stepChecker?: IReactionDisposer
  @observable accessor currentStepId = ''
  @observable accessor didAction: ManualStepAction | undefined
  @observable accessor recentlyCompletedStep = {
    atTime: 0,
    stepId: '',
  }

  constructor(rootStore: $RootStore) {
    this.rootStore = rootStore

    if (rootStore.config.name === 'development') {
      validateStepData(steps, [])
    }
  }

  checkGetStartedSteps = async () => {
    if (this.visibleSteps.length === 0) return

    const { externalTreatmentStore, userStore } = this.rootStore

    const { saasSettings, completedSetupSteps } = userStore.data.professionalProfile

    const stepsToComplete: string[] = []

    // Treatments check
    if (externalTreatmentStore.items.length > 0 && !completedSetupSteps.includes('GettingStarted-treatments')) {
      stepsToComplete.push('GettingStarted-treatments')
    }

    // Locations check
    const hasEnabledLocation =
      (saasSettings.staticLocations || []).filter(s => s.enabled).length > 0 || saasSettings.mobileLocation?.enabled
    if (hasEnabledLocation && !completedSetupSteps.includes('GettingStarted-locations')) {
      stepsToComplete.push('GettingStarted-locations')
    }

    // Website check
    if (saasSettings.isPublic && !completedSetupSteps.includes('GettingStarted-website')) {
      stepsToComplete.push('GettingStarted-website')
    }

    for (let i = 0; i < stepsToComplete.length; i++) {
      const isLast = i === stepsToComplete.length - 1
      await this.stepCompleted(stepsToComplete[i], isLast, false, !isLast)
    }
  }

  markStepCompleted = (stepId: string) => {
    if (!this.completedStepIds.includes(stepId)) {
      this.stepCompleted(stepId, false)
    }
  }

  showTaskCompletedPopup(_params: {
    relatedTask: SetupTaskData
    navigationStore: $RootStore['navigationStore']
    bottomMessageHandler: $RootStore['bottomMessageHandler']
  }): void { }

  onStepSetup(): void { }

  onStepSkip(): void { }

  showStepModalPopup(_params: {
    step: SetupStep
    isRequiredStep: boolean
    isDev: boolean
    bottomMessageHandler: $RootStore['bottomMessageHandler']
    onSetup: () => void
    onSkip: () => void
  }): void { }

  openURL(_url: string): void { }

  showStepCompletedToast(_toastData: ToastData): void { }

  getPlatformStepData(): PlatformStepData[] {
    return []
  }

  skipStep = (stepId: string) => {
    this.stepCompleted(stepId, false, true)
  }

  /**
   * Starts a setup step:
   * 1. Validates the step exists and has a completion check
   * 2. Sets up a MobX `when()` reaction that auto-completes the step once `check()` returns true
   * 3. Executes the step's start action (e.g. navigate to a screen, open a modal)
   */
  startStep = async (stepId: string) => {
    const step = steps.find(s => s.id === stepId)
    const check = this.getStepCheck(stepId)

    // Step must exist and have a check function to detect completion
    if (!step || !check) return

    // Observable mutations must be in runInAction; analytics/async work stays outside
    runInAction(() => {
      this.currentStepId = stepId
      this.didAction = undefined
      this.stepChecker?.()
      // Watch for step completion condition and auto-complete when met
      this.stepChecker = when(
        () => check(this.rootStore),
        () => this.stepCompleted(stepId),
      )
    })

    analytics.track('Setup Step Started', {
      stepId,
      stepData: step,
    })

    try {
      await this.executeStartAction(stepId)
    } catch (err) {
      console.error('executeStartAction failed for step', stepId, err)
    }
  }

  findPlatformStepData = (stepId: string): PlatformStepData | undefined => {
    return this.getPlatformStepData().find(step => step.id === stepId)
  }

  async executeStartAction(stepId: string) {
    const platformData = this.findPlatformStepData(stepId)

    if (platformData?.startAction) {
      await platformData.startAction(this.rootStore)
    } else {
      // Fallback to step's default startAction
      const step = steps.find(s => s.id === stepId)
      await step?.startAction(this.rootStore)
    }
  }

  getStepCheck = (stepId: string): ((rootStore: $RootStore) => boolean) | undefined => {
    const platformData = this.findPlatformStepData(stepId)
    if (platformData?.check) {
      return platformData.check
    }
    const step = steps.find(s => s.id === stepId)
    return step?.check
  }

  stepCompleted = (stepId: string, showToast = true, skipped = false, skipSave = false) => {
    const { userStore, bottomMessageHandler, navigationStore } = this.rootStore

    const stepData = steps.find(step => step.id === stepId)

    runInAction(() => {
      if (!userStore.data.professionalProfile.completedSetupSteps.includes(stepId)) {
        userStore.data.professionalProfile.completedSetupSteps.push(stepId)
      }
    })

    const { toastData } = stepData || {}

    const relatedTask = stepData
      ? setupTasksData.find(task => task.taskId === stepData.taskId)
      : undefined
    const taskStatus = relatedTask
      ? getTaskStatus(this.getStepsForTask(relatedTask.taskId), this.completedStepIds)
      : ''

    if (showToast) {
      if (taskStatus === 'COMPLETED' && relatedTask?.successPopupData) {
        this.showTaskCompletedPopup({
          relatedTask,
          navigationStore,
          bottomMessageHandler,
        })
      } else if (toastData) {
        const resolvedToastData = typeof toastData === 'function' ? toastData(this.rootStore) : toastData
        this.showStepCompletedToast(resolvedToastData)
      }
    }

    runInAction(() => {
      this.recentlyCompletedStep = {
        atTime: Date.now(),
        stepId,
      }
      this.currentStepId = ''
    })

    analytics.track('Setup Step Completed', {
      stepId,
      skipped,
      stepData,
    })

    if (!skipSave) {
      this.saveSetupProgress()
    }
  }

  getStepsForTask = (taskId: SetupTaskId) => {
    return this.getTaskSteps(taskId).map(step => step.id)
  }

  getTaskSteps = (taskId: SetupTaskId) => {
    return this.visibleSteps.filter(step => step.taskId === taskId)
  }

  isRequiredStep = (step: SetupStep) => {
    return !step.canSkip && this.gettingStartedFirstSteps.some(s => s.id === step.id)
  }

  getStepOnPress = (stepId: string, completed: boolean): (() => void) | undefined => {
    if (completed) {
      return this.rootStore.config.name === 'development'
        ? () => this.resetStep(stepId)
        : undefined
    }
    return () => this.showStepModal(stepId)
  }

  showStepModal = (stepId: string) => {
    const step = steps.find(s => s.id === stepId)
    if (!step) return
    const { bottomMessageHandler } = this.rootStore
    const isRequiredStep = this.isRequiredStep(step)
    const isDev = this.rootStore.config.name === 'development'

    this.showStepModalPopup({
      step,
      isRequiredStep,
      isDev,
      bottomMessageHandler,
      onSetup: () => {
        this.onStepSetup()
        this.startStep(stepId)
      },
      onSkip: () => {
        this.onStepSkip()
        this.skipStep(stepId)
      },
    })
  }

  resetStep = async (stepId: string) => {
    const data = steps.find(step => step.id === stepId)
    if (!data) {
      return
    }

    const platformData = this.findPlatformStepData(stepId)
    if (platformData?.__reset) {
      await platformData.__reset(this.rootStore)
    } else if (data.__reset) {
      await data.__reset(this.rootStore)
    }

    runInAction(() => {
      const { professionalProfile } = this.rootStore.userStore.data
      professionalProfile.completedSetupSteps = professionalProfile.completedSetupSteps.filter(step => step !== stepId)
    })
    this.saveSetupProgress()
  }

  triggerAction = (action: ManualStepAction) => {
    runInAction(() => {
      this.didAction = action
    })
  }

  saveSetupProgress = async () => {
    const { userStore } = this.rootStore
    runInAction(() => {
      userStore.data.professionalProfile.completedSetupTasks = this.completedTasks.map(task => task.taskId)
    })

    const { completedSetupSteps, completedSetupTasks } = userStore.data.professionalProfile

    await userStore.updateProfessionalProfile_api({
      completedSetupSteps,
      completedSetupTasks,
    })
  }

  @computed get currentStepData() {
    return steps.find(step => step.id === this.currentStepId)
  }

  @computed get skippedStepIdsForPlatform() {
    const platform = this.rootStore.config.platform
    return steps
      .filter(step => step.skipOnPlatforms?.includes(platform))
      .map(step => step.id)
  }

  @computed get completedStepIds() {
    const userCompleted = this.rootStore.userStore.data.professionalProfile.completedSetupSteps || []
    // Include steps that are skipped on this platform
    return [...new Set([...userCompleted, ...this.skippedStepIdsForPlatform])]
  }

  @computed get setupTasksWithStatus() {
    return setupTasksData.map(task => {
      return {
        ...task,
        status: getTaskStatus(this.getStepsForTask(task.taskId), this.completedStepIds),
      }
    })
  }

  @computed get completedTasks() {
    return this.setupTasksWithStatus.filter(task => task.status === 'COMPLETED')
  }

  @computed get nextTask() {
    const inCompleteTasks = this.setupTasksWithStatus.filter(task => task.status !== 'COMPLETED')
    return inCompleteTasks[0]
  }

  @computed get inProgressTaskData() {
    if (!this.nextTask) return

    const { taskId, title } = this.nextTask

    const isSetupTask = [SetupTaskId.GettingStarted].includes(taskId)

    if (!isSetupTask) return

    const taskStepIds = this.getStepsForTask(taskId)
    const completedTaskStepIds = taskStepIds.filter(id => this.completedStepIds.includes(id))

    return {
      taskId,
      title,
      stepsCompleted: completedTaskStepIds.length,
      stepsTotal: taskStepIds.length,
    }
  }

  @computed get visibleSteps() {
    if (!hasCapability(sessionAccessOf(this.rootStore), 'setupChecklist')) return []
    return steps.filter(step => !step.hidden?.(this.rootStore))
  }

  @computed get remainingTasks() {
    return this.visibleSteps.filter(step => !this.completedStepIds.includes(step.id))
  }

  @computed get nextSetupStep(): SetupStep | null {
    return this.remainingTasks[0] ?? null
  }

  @computed get gettingStartedFirstSteps() {
    return this.getTaskSteps(SetupTaskId.GettingStarted)
  }

  @computed get remainingGettingStartedSteps() {
    return this.gettingStartedFirstSteps.filter(step => !this.completedStepIds.includes(step.id))
  }

  @computed get completedGettingStartedSteps() {
    return this.gettingStartedFirstSteps.filter(step => this.completedStepIds.includes(step.id))
  }

  @computed get gettingStartedFirstStepsComplete() {
    return this.remainingGettingStartedSteps.length === 0
  }

  clearData(): void {
    this.stepChecker?.()
    runInAction(() => {
      this.stepChecker = undefined
      this.currentStepId = ''
      this.didAction = undefined
      this.recentlyCompletedStep = {
        atTime: 0,
        stepId: '',
      }
    })
  }

  @computed get inCompleteWebsiteSections(): Record<WebEditorModal[number], boolean> {
    const { externalReviewStore, userStore: { data: { professionalProfile } } } = this.rootStore

    const lookBookComplete = (professionalProfile.saasSettings.lookBook?.length ?? 0) > 0
    const pinnedReviewComplete = professionalProfile.saasSettings.pinnedReviewId
    const reviewsComplete = professionalProfile.externalReviewCount > 0 || externalReviewStore.items.length > 0
    const contactComplete = (professionalProfile.saasSettings.contactDetails?.length ?? 0) > 0
    const infoComplete = professionalProfile.saasSettings.info?.length > 0

    return {
      lookbook: !lookBookComplete,
      'pinned-review': !pinnedReviewComplete,
      reviews: !reviewsComplete,
      contact: !contactComplete,
      info: !infoComplete,
    }
  }

  @computed get gmbAllStepsCompleted() {
    return gmbOnboardingSteps.every(step => this.completedStepIds.includes(step.id))
  }
}

export default BaseSetupStore
export { BaseSetupStore }
export type { ManualStepAction }
